import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "./Analytics.css";
import { UserData } from "../model/user";
import { useEffect, useState } from "react";
import { Scout, ScoutStatus } from "../organisation/model/scout";
import { CenteredRow } from "../layout/Row";
import BorderButton from "../button/BorderButton";
import { SmallLogoWithDashboard } from "../logo/SmallLogo";
import LogoutWidget from "../logout/LogoutWidget";
import NotificationOverlay from "../status/NotificationOverlay";
import { queryScouts } from "../organisation/firestore/scouts";
import { ColumnCentered } from "../layout/Column";
import { connect } from "react-redux";
import { getResponseAndProcessingTimes, ProcessingResponseTimes } from "../services/adminService";
import LoadingAnimation from "../animation/LoadingAnimation";
import ProcessingAndResponseTimesChart from "../plot/ProcessingAndResponseTimesChart";


enum ProcessingResponseTimeRange {
    one_day = 24 * 60,
    one_hour = 60,
    half_hour = 30,
    ten_minutes = 10
}


const ProcessingResponeTimesAnalyticsPage = (props: Props) => {
    //@ts-ignore
    const deviceId: string | undefined = props.match.params.deviceId;
    //@ts-ignore
    const organisationId: string | undefined = props.match.params.organisationId;

    const [timeRange, setTimeRange] = useState<ProcessingResponseTimeRange>(ProcessingResponseTimeRange.half_hour);
    const [fromDate, setFromDate] = useState<Date>(getFromDate(ProcessingResponseTimeRange.half_hour, new Date()));
    const [toDate, setToDate] = useState<Date>(new Date());
    const [liveUpdate, setLiveUpdate] = useState<boolean>(true);

    useEffect(() => {
        if (liveUpdate) {
            const interval = setInterval(() => {
                setFromDate(getFromDate(timeRange, new Date()));
                setToDate(new Date());
            }, 30 * 1000);
            return () => clearInterval(interval);
        }
    }, [liveUpdate, timeRange]);
    return (
        <div className="Dashboard">
            <div className={"Dashboard-header"}>
                <SmallLogoWithDashboard fontSizeRem={1.625} />
                <LogoutWidget {...props} />
            </div>
            <NotificationOverlay />
            <CenteredRow style={{ paddingBottom: "2.5rem" }}>
                <h1>Processing/Response times</h1>
            </CenteredRow>
            <TimeRangeSelection setTimeRange={setTimeRange} timeRange={timeRange} setFromDate={setFromDate} toDate={toDate} />
            <CenteredRow>
                <PickDate toDate={toDate} setToDate={setToDate} setFromDate={setFromDate} timeRange={timeRange} setLiveUpdate={setLiveUpdate} />
            </CenteredRow>
            <main className={"Graph-content"} style={{ maxWidth: "100vw", paddingLeft: "100" }}>
                {deviceId !== undefined ? (
                    <DeviceProcessingResponsePage
                        id={deviceId}
                        timeRange={timeRange}
                        fromDate={fromDate}
                        toDate={toDate}
                        {...props}
                    />
                ) : organisationId !== undefined ? (
                    <OrganisationProcessingResponsePage
                        id={organisationId}
                        timeRange={timeRange}
                        fromDate={fromDate}
                        toDate={toDate}
                        {...props}
                    />
                ) : (
                    <div>Missing device or organisation id</div>
                )}
            </main>
        </div>
    )
}

const OrganisationProcessingResponsePage = (props: ProcessingResponseAnalyticsProps) => {
    const organisationId = props.id;
    const [scouts, setScouts] = useState<Scout[]>([]);
    useEffect(() => {
        if (props.jwtToken) {
            queryScouts(organisationId, (s) => {
                s.sort((a, b) => a.status.displayName.localeCompare(b.status.displayName));
                setScouts(s);
            });
        }
    }, []);
    return (
        <>
            {scouts.map((scout) => (
                <DeviceProcessingResponsePage {...props} id={scout.deviceId} scout={scout.status} />
            ))}
        </>
    );
};

const DeviceProcessingResponsePage = (props: ProcessingResponseAnalyticsProps) => {
    const deviceId = props.id;
    const fromDate = props.fromDate
    const toDate = props.toDate;
    const [measurements, setMeasurements] = useState<ProcessingResponseTimes>();
    const [filteredMeasurements, setFilteredMeasurements] = useState<ProcessingResponseTimes>();
    const [loading, setLoading] = useState<boolean>(false);
    const [displayProcessingSpeed, setDisplayProcessingSpeed] = useState<boolean>(true);
    const [displayResponseTime, setDisplayResponseTime] = useState<boolean>(true);

    useEffect(() => {
        if (props.jwtToken) {
            setLoading(true);
            getResponseAndProcessingTimes(props.jwtToken, deviceId, fromDate, toDate)
                .then((data) => {
                    if (data.timestamps.length === 0) {
                        console.log("No Response measurements found for device", deviceId, "in the specified time range.");
                        setMeasurements(undefined);
                    } else {
                        setMeasurements(data);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching battery measurements:", error);
                });
        }


    }, [props.jwtToken, props.fromDate]);

    useEffect(() => {
        if (measurements === undefined) {
            return;
        }
        setFilteredMeasurements(() => {
            return {
                ...measurements,
                processingTimes: displayProcessingSpeed ? measurements.processingTimes : [],
                responseTimes: displayResponseTime ? measurements.responseTimes : [],
            };
        });
    }, [displayProcessingSpeed, displayResponseTime, measurements]);

    if (filteredMeasurements === undefined) {
        return <div style={{ marginBottom: "70px" }} >{loading && <CenteredRow> <LoadingAnimation height={40} width={40} color="blue" /></CenteredRow>}</div>
    }
    return (
        <>
            <ColumnCentered>
                <h2>Device {props.scout?.displayName ?? deviceId} • {props.scout?.deviceInfo.model ?? "NA"} • SDK: {props.scout?.deviceInfo.sdk ?? "NA"} • {props.scout?.appVersion ?? "NA"}
                    {loading && <span> <LoadingAnimation height={18} width={18} color="blue" /></span>}
                </h2>
                <CenteredRow style={{ paddingBottom: "2.5rem" }}>
                    <BorderButton
                        text="Processing Speed"
                        onClick={() => setDisplayProcessingSpeed(!displayProcessingSpeed)}
                        selected={displayProcessingSpeed}
                    />
                    <BorderButton
                        text="Response Time"
                        onClick={() => setDisplayResponseTime(!displayResponseTime)}
                        selected={displayResponseTime}
                    />
                </CenteredRow>
                <ProcessingAndResponseTimesChart graphData={filteredMeasurements} />
            </ColumnCentered>
        </>
    )

}

function TimeRangeSelection({ setTimeRange, timeRange, setFromDate, toDate }: { setTimeRange: (ProcessingResponseTimeRange) => void, timeRange: ProcessingResponseTimeRange; setFromDate: (date: Date) => void; toDate: Date }) {
    return (
        <CenteredRow style={{ paddingBottom: "2.5rem" }}>
            <BorderButton
                key={0}
                text="1 day"
                onClick={() => {
                    setFromDate(getFromDate(ProcessingResponseTimeRange.one_day, toDate))
                    setTimeRange(ProcessingResponseTimeRange.one_day);
                }}
                selected={timeRange === ProcessingResponseTimeRange.one_day}
            />
            <BorderButton
                key={1}
                text="1 hour"
                onClick={() => {
                    setFromDate(getFromDate(ProcessingResponseTimeRange.one_hour, toDate))
                    setTimeRange(ProcessingResponseTimeRange.one_hour);
                }}
                selected={timeRange === ProcessingResponseTimeRange.one_hour}
            />
            <BorderButton
                key={2}
                text="30 minutes"
                onClick={() => {
                    setFromDate(getFromDate(ProcessingResponseTimeRange.half_hour, toDate))
                    setTimeRange(ProcessingResponseTimeRange.half_hour);
                }}
                selected={timeRange === ProcessingResponseTimeRange.half_hour}
            />
            <BorderButton
                key={3}
                text="10 minutes"
                onClick={() => {
                    setFromDate(getFromDate(ProcessingResponseTimeRange.ten_minutes, toDate))
                    setTimeRange(ProcessingResponseTimeRange.ten_minutes);
                }}
                selected={timeRange === ProcessingResponseTimeRange.ten_minutes}
            />
        </CenteredRow>
    );
}

function PickDate({
    toDate,
    setToDate,
    setFromDate,
    timeRange,
    setLiveUpdate
}: {
    toDate: Date,
    setToDate: (date: Date) => void,
    setFromDate: (date: Date) => void,
    timeRange: ProcessingResponseTimeRange
    setLiveUpdate: (liveUpdate: boolean) => void
}) {
    return (
        <CenteredRow style={{ paddingBottom: "2.5rem" }}>
            <input
                type="datetime-local"
                value={localeStringToDatetimeLocal(toDate.toLocaleString())} // "YYYY-MM-DDTHH:mm"
                onChange={(e) => {
                    const selectedDate = new Date(e.target.value);
                    console.log("Selected date & time:", selectedDate);

                    setToDate(selectedDate);
                    setFromDate(getFromDate(timeRange, selectedDate));
                    setLiveUpdate(false); // Disable live update when a custom date is selected
                }}
            />
        </CenteredRow>
    );
}

const mapStateToProps = (state: any): StateToProps => ({
    jwtToken: state.login.jwtToken,
    user: state.login.user,
    userData: state.login.userData,
});

const mapDispatchToProps: DispatchFromProps = {
    setUser: (_: firebase.User | undefined) => { },
    setUserData: (_: UserData | undefined) => { },
};

export default connect<StateToProps, DispatchFromProps>(
    // @ts-ignore
    mapStateToProps,
    mapDispatchToProps
)(ProcessingResponeTimesAnalyticsPage);

interface StateToProps {
    jwtToken: string | undefined;
    user: firebase.User | undefined;
    userData: UserData | undefined;
}

interface DispatchFromProps {
    setUser: (_: firebase.User | undefined) => void;
    setUserData: (_: UserData | undefined) => void;
}

interface Props extends StateToProps, DispatchFromProps { }

interface ProcessingResponseAnalyticsProps extends Props {
    id: string;
    timeRange: ProcessingResponseTimeRange;
    fromDate: Date;
    toDate: Date;
    scout?: ScoutStatus;
}

function getFromDate(timeRange: ProcessingResponseTimeRange, toDate: Date): Date {
    return new Date(toDate.getTime() - timeRange * 60 * 1000);
}

function localeStringToDatetimeLocal(localeString) {
    // Example inputs:
    // "7/10/2025, 14:34"
    // "7/10/2025, 2:34 PM"

    const [datePart, timePartRaw] = localeString.split(',').map(s => s.trim());
    const [month, day, year] = datePart.split('/').map(num => parseInt(num, 10));
    
    let [timePart, modifier] = timePartRaw.split(' '); // modifier may be undefined for 24h
    let [hours, minutes] = timePart.split(':').map(num => parseInt(num, 10));

    // Convert to 24h format if needed
    if (modifier) {
        const isPM = modifier.toLowerCase() === 'pm';
        if (isPM && hours < 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;
    }

    // Pad everything
    const MM = String(month).padStart(2, '0');
    const DD = String(day).padStart(2, '0');
    const HH = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');

    return `${year}-${MM}-${DD}T${HH}:${mm}`;
}

